import Table from "components/DataTable/Table";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import Spinner from "components/Spinner";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import CouriersAdd from "./CouriersAdd";
import CouriersUpdate from "./CouriersUpdate";
import { trackingSummary } from "./TrackingApiFields";

function Couriers() {
  const user = useSelector((state) => state.user.userInfo);
  /*   All States Below
   ********************************************* */
  const [couriers, setCouriers] = useState(null);
  const [lastSortId, setLastSortId] = useState(null);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // below update data is used to set a variable before moving to update component
  const [updateData, setUpdateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState();

  /*   All Functions Below
   ********************************************* */

  // get All couriers
  const getAllCouriers = async () => {
    try {
      const { data } = await API.get("/courier-services", {
        headers: authHeader(),
      });
      setCouriers(data);

      setLastSortId(
        data[data.length - 1]?.sortId ? data[data.length - 1]?.sortId : 0
      );
    } catch (err) {
      alert(err.message);
    }
  };

  // Delete password Data
  const deleteCouriersData = async (id) => {
    const { data } = await API.delete("/courier-service/delete/" + id, {
      headers: authHeader(),
    });
    if (data.message.type == "success") {
      getAllCouriers();
      return;
    }
  };

  // Sorting Up
  const sortUp = async (index) => {
    const currentSortId = couriers[index].sortId;
    const currentId = couriers[index].id;
    const upSortId = couriers[index - 1].sortId;
    const upId = couriers[index - 1].id;
    try {
      const { data } = await API.post(
        "/courier-service/sortup",
        { currentSortId, currentId, upSortId, upId },
        { headers: authHeader() }
      );
      if (data.success) {
        getAllCouriers();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (index) => {
    const currentSortId = couriers[index].sortId;
    const currentId = couriers[index].id;
    const downSortId = couriers[index + 1].sortId;
    const downId = couriers[index + 1].id;
    try {
      const { data } = await API.post(
        "/courier-service/sortDown",
        { currentSortId, currentId, downSortId, downId },
        { headers: authHeader() }
      );
      if (data.success) {
        getAllCouriers();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const setDefaultCourierService = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/courier-service/set-default",
        { id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getAllCouriers();
      }
      setLoading(false);
    } catch (err) {
      alert(err.message);
      setLoading(false);
    }
  };

  // Tracking Info: the one courier rows from the WorldShip watcher belong to.
  // The server keeps it to one per company.
  const setUpsTracker = async (id, isUpsTracker) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/courier-service/set-ups-tracker",
        { id, isUpsTracker },
        { headers: authHeader() }
      );
      if (data.success) {
        await getAllCouriers();
      }
    } catch (err) {
      alert(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllCouriers();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  /*   Table Functions below
   ********************************************* */
  const buttons = () => (
    <div className="d-flex justify-content-end ms-2">
      <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
        ADD NEW
      </button>
    </div>
  );

  const table_head = [
    "Logo",
    "Courier Services",
    "Tracking URL",
    "Tracking API",
    "UPS Tracker",
    "Is Default",
    "Actions",
  ];
  const table_body = (item, index) => (
    <tr key={item.id}>
      <td style={{ width: 80 }}>
        {item.logo ? (
          <ImageWIthSignedUrl
            path={item.logo}
            alt={`${item.name || "Courier"} logo`}
            imgStyle={{ maxHeight: 28, width: "auto", objectFit: "contain" }}
          />
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td>{item.name}</td>
      {/* Cut short (full URL on hover) so the buttons to the right keep their
          width and stay on one line (user, 2026-10-19). */}
      <td className="text-truncate" style={{ maxWidth: 260 }} title={item.trackingUrl || ""}>
        {item.trackingUrl || <span className="text-muted">—</span>}
      </td>
      {/* Never the credentials themselves -- the API sends a last-4 hint. */}
      <td className="text-nowrap">{trackingSummary(item)}</td>
      <td>
        {item.isUpsTracker ? (
          <span className="d-inline-flex align-items-center gap-1">
            <span className="badge bg-success">UPS tracker</span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-muted"
              title="Stop treating this courier as the UPS tracker"
              aria-label={`Clear UPS tracker on ${item.name}`}
              onClick={() => setUpsTracker(item.id, false)}
            >
              <i className="mdi mdi-close"></i>
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setUpsTracker(item.id, true)}
            className="btn btn-outline-success btn-sm py-0 px-2 text-nowrap"
          >
            Set UPS Tracker
          </button>
        )}
      </td>
      <td>{item.isDefault ? "Yes" : "No"}</td>
      <td className="table-action text-end text-nowrap">
        {!item?.isDefault && (
          <button
            onClick={() => setDefaultCourierService(item.id)}
            className="btn btn-info btn-sm py-0 px-2 fs-5 text-nowrap"
          >
            Set Default
          </button>
        )}
        {user?.roles === "admin" && (
          <Link
            onClick={() => {
              item.sortId != 1 && sortUp(index);
            }}
            className="action-icon"
          >
            <i className="mdi mdi-chevron-double-up"></i>
          </Link>
        )}
        {user?.roles === "admin" && (
          <Link
            onClick={() => {
              item.sortId != couriers.length && sortDown(index);
            }}
            className="action-icon"
          >
            <i className="mdi mdi-chevron-double-down"></i>
          </Link>
        )}
        <Link className="action-icon">
          <i
            onClick={() => {
              setShowUpdateModal(true);
              setUpdateData(item);
            }}
            className="mdi mdi-pencil"
          ></i>
        </Link>
        <Link className="action-icon">
          <i
            data-bs-toggle="modal"
            data-bs-target="#danger-alert-modal"
            className="mdi mdi-delete"
            onClick={() => setSelectedDelete(item)}
          ></i>
        </Link>
      </td>
    </tr>
  );

  return (
    <div className="row">
      <h3>Courier Services</h3>
      {loading && <Spinner />}
      <div className="col-12">
        <Table
          btn1={buttons}
          table_body={table_body}
          table_data={couriers}
          table_head={table_head}
          actionsOnEnd={true}
          columnSortable={false}
        />

        {/*   ADD Modal
         ********************************************* */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
          <Modal.Body className="custom-shadow">
            <div>
              <CouriersAdd
                showAddPage={setShowAddModal}
                refreshFunc={getAllCouriers}
                lastSortId={lastSortId}
              />
            </div>
          </Modal.Body>
        </Modal>
        {/*   Update Modal
         ********************************************* */}
        <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
          <Modal.Body className="custom-shadow">
            <div>
              <CouriersUpdate
                data={updateData}
                showUpdatePage={setShowUpdateModal}
                refreshFunc={getAllCouriers}
              />
            </div>
          </Modal.Body>
        </Modal>
        {/*   Confirmation Modal
         ********************************************* */}

        <div
          id="danger-alert-modal"
          className="modal fade"
          tabIndex="-1"
          role="dialog"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-sm ">
            <div className="modal-content modal-filled bg-danger">
              <div className="modal-body p-4">
                <div className="text-center">
                  <i className="ri-close-circle-line h1"></i>
                  <h4 className="mt-2">Confirm Delete!</h4>
                  <p className="mt-3">Do You want to delete ?</p>
                  <button
                    type="button"
                    className="btn btn-light my-2 mx-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-light my-2 "
                    data-bs-dismiss="modal"
                    onClick={() => {
                      deleteCouriersData(selectedDelete.id);
                    }}
                  >
                    DELETE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Couriers;
